import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Heart, 
  Zap, 
  Shield, 
  Plane, 
  Brain, 
  Users, 
  Home,
  ChevronRight,
  CheckCircle,
  Lock,
  Clock,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { getPublicStoreCatalog, type StoreCatalogItem } from '../lib/db-storage';
import { projectId, publicAnonKey } from '/utils/supabase/info';

interface Pack {
  id: string;
  name: string;
  price: number;
  icon: any;
  color: string;
  bgColor: string;
  description: string;
  features: string[];
  duration: string;
  includes: {
    dailyActions: number;
    exercises: number;
    trackers: number;
    resources: number;
  };
}

interface EnhancedSituationalPacksProps {
  tier: 'free' | 'premium' | 'pro';
}

function findCatalogPack(items: StoreCatalogItem[], packId: string): StoreCatalogItem | undefined {
  const pid = packId.toLowerCase();
  return items.find((i) => {
    if (i.kind !== 'pack' || i.active === false || !i.stripe_price_id) return false;
    const slugNorm = String(i.slug || '').replace(/^pack-/, '').toLowerCase();
    if (slugNorm === pid || String(i.slug).toLowerCase() === pid) return true;
    const ps = i.pack_slugs;
    return Array.isArray(ps) && ps.some((s) => String(s).toLowerCase() === pid);
  });
}

export function EnhancedSituationalPacks({ tier }: EnhancedSituationalPacksProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const user = auth?.user ?? null;
  const accessToken = auth?.accessToken ?? null;
  const [catalogItems, setCatalogItems] = useState<StoreCatalogItem[]>([]);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState<string | null>(null);

  useEffect(() => {
    getPublicStoreCatalog()
      .then((r) => setCatalogItems(r.items || []))
      .catch(() => setCatalogItems([]));
  }, []);

  const catalogPriceDollars = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of [
      'breakup-recovery',
      'reconnection',
      'conflict-repair',
      'long-distance',
      'anxious-attachment',
      'meeting-family',
      'moving-in',
    ]) {
      const row = findCatalogPack(catalogItems, p);
      if (row?.amount_cents != null) m[p] = row.amount_cents / 100;
    }
    return m;
  }, [catalogItems]);

  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [purchasedPacks, setPurchasedPacks] = useState<string[]>(() => {
    return JSON.parse(localStorage.getItem('tension_purchased_packs') || '[]');
  });

  const isPro = tier === 'pro';

  const packs: Pack[] = [
    {
      id: 'breakup-recovery',
      name: 'Breakup Recovery',
      price: isPro ? 0 : (catalogPriceDollars['breakup-recovery'] ?? 2.99),
      icon: Heart,
      color: 'text-red-600',
      bgColor: 'from-red-50 to-pink-50',
      description: 'Navigate the aftermath of a breakup with structured healing',
      duration: '30-day program',
      features: [
        '30-day no-contact tracker with daily encouragement',
        'Grief stage identification (denial, anger, bargaining, depression, acceptance)',
        'Emergency "Want to Text" intervention protocols',
        'Self-worth rebuilding exercises and affirmations',
        'Decision tree: "When is it okay to reach out?"',
        'Healing milestone celebrations',
        'Relapse prevention strategies',
      ],
      includes: {
        dailyActions: 30,
        exercises: 15,
        trackers: 5,
        resources: 20,
      },
    },
    {
      id: 'reconnection',
      name: 'Reconnection Protocol',
      price: isPro ? 0 : (catalogPriceDollars['reconnection'] ?? 2.99),
      icon: Zap,
      color: 'text-yellow-600',
      bgColor: 'from-yellow-50 to-orange-50',
      description: 'Rebuild connection after conflict or distance',
      duration: '14-day intensive',
      features: [
        'Post-fight repair scripts and conversation starters',
        'Effective vs. ineffective apology framework',
        'Trust rebuilding timeline and milestones',
        'Vulnerability exercises for emotional reconnection',
        '"Testing the waters" conversation guides',
        'Attachment repair strategies',
        'Communication pattern reset',
      ],
      includes: {
        dailyActions: 14,
        exercises: 10,
        trackers: 3,
        resources: 12,
      },
    },
    {
      id: 'conflict-repair',
      name: 'Conflict Repair',
      price: isPro ? 0 : (catalogPriceDollars['conflict-repair'] ?? 2.99),
      icon: Shield,
      color: 'text-blue-600',
      bgColor: 'from-blue-50 to-cyan-50',
      description: 'Transform conflict patterns into healthy resolution',
      duration: '21-day course',
      features: [
        'Fight pattern analysis (identify your triggers)',
        'De-escalation techniques for heated moments',
        'Repair attempt tracker (Did you apologize? Did they?)',
        'Cooling-off period timer with guidance',
        'Conflict resolution scripts and frameworks',
        'Emotional regulation exercises',
        'Post-conflict reconnection rituals',
      ],
      includes: {
        dailyActions: 21,
        exercises: 12,
        trackers: 4,
        resources: 15,
      },
    },
    {
      id: 'long-distance',
      name: 'Long Distance',
      price: isPro ? 0 : (catalogPriceDollars['long-distance'] ?? 2.99),
      icon: Plane,
      color: 'text-purple-600',
      bgColor: 'from-purple-50 to-pink-50',
      description: 'Maintain intimacy across distance',
      duration: 'Ongoing support',
      features: [
        'Visit countdown tracker with excitement builder',
        'Daily connection rituals for LDR couples',
        'Time zone-aware communication scheduler',
        'Virtual date ideas and activities',
        'Trust maintenance exercises',
        'Reunion preparation guide',
        'Managing expectation mismatches',
      ],
      includes: {
        dailyActions: 30,
        exercises: 8,
        trackers: 5,
        resources: 18,
      },
    },
    {
      id: 'anxious-attachment',
      name: 'Anxious Attachment',
      price: isPro ? 0 : (catalogPriceDollars['anxious-attachment'] ?? 2.99),
      icon: Brain,
      color: 'text-indigo-600',
      bgColor: 'from-indigo-50 to-purple-50',
      description: 'Shift from anxious to secure attachment',
      duration: '60-day transformation',
      features: [
        'Anxious pattern identification and tracking',
        'Self-soothing techniques (don\'t seek external validation)',
        'Core wound healing exercises',
        'Independence and self-worth building',
        'Cognitive reframing for catastrophic thoughts',
        'Secure relationship modeling',
        'Progress tracking: anxious → secure behaviors',
      ],
      includes: {
        dailyActions: 60,
        exercises: 25,
        trackers: 8,
        resources: 30,
      },
    },
    {
      id: 'meeting-family',
      name: 'Meeting the Family',
      price: isPro ? 0 : (catalogPriceDollars['meeting-family'] ?? 2.99),
      icon: Users,
      color: 'text-green-600',
      bgColor: 'from-green-50 to-emerald-50',
      description: 'Navigate the critical family introduction milestone',
      duration: '7-day prep + support',
      features: [
        'Pre-meeting preparation checklist',
        'Conversation topic ideas and icebreakers',
        'Boundary-setting with partner beforehand',
        'Post-meeting debrief framework',
        'Managing family criticism or acceptance',
        'Red flag identification (family dynamics)',
        'Relationship progression after family approval',
      ],
      includes: {
        dailyActions: 7,
        exercises: 5,
        trackers: 2,
        resources: 10,
      },
    },
    {
      id: 'moving-in',
      name: 'Moving In Together',
      price: isPro ? 0 : (catalogPriceDollars['moving-in'] ?? 2.99),
      icon: Home,
      color: 'text-orange-600',
      bgColor: 'from-orange-50 to-red-50',
      description: 'Successfully transition to cohabitation',
      duration: '30-day transition',
      features: [
        'Pre-move-in conversation framework (finances, chores, space)',
        'Cohabitation agreement template',
        'Personal space preservation strategies',
        'Conflict navigation when living together',
        'Maintaining romance and mystery',
        'Chore division tracker',
        'Adjustment period milestones',
      ],
      includes: {
        dailyActions: 30,
        exercises: 10,
        trackers: 6,
        resources: 15,
      },
    },
  ];

  const handlePurchase = async (packId: string) => {
    setSelectedPack(packId);

    if (isPro) {
      // Pro users get all packs free
      const updated = [...purchasedPacks, packId];
      setPurchasedPacks(updated);
      localStorage.setItem('tension_purchased_packs', JSON.stringify(updated));
      toast.success('Pack activated! (Included with Pro)');
      return;
    }

    const cat = findCatalogPack(catalogItems, packId);
    if (!cat?.stripe_price_id) {
      toast.error(
        'This pack is not available for purchase yet. Enable it in Admin → Storefront (Stripe price required).',
      );
      return;
    }

    if (!user) {
      toast.error('Please sign in to purchase');
      navigate('/auth');
      return;
    }
    if (!accessToken) {
      toast.error('Session expired. Please sign in again.');
      navigate('/auth');
      return;
    }

    setCheckoutLoadingId(packId);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/create-pack-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
            'X-User-Token': accessToken,
          },
          body: JSON.stringify({
            packIds: [packId],
            packType: 'relationship',
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.error || 'Checkout failed');
        return;
      }
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success('Opening Stripe checkout…');
      } else {
        toast.error('No checkout URL received');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error. Try again.');
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  const handleActivatePack = (packId: string) => {
    localStorage.setItem('tension_active_pack', packId);
    toast.success('Situational pack activated! Check your action cards.');
    // Would navigate to pack-specific dashboard
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Zap className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 mb-1">Situational Action Packs</h3>
            <p className="text-sm text-neutral-600 mb-2">
              Specialized daily action plans for specific relationship situations
            </p>
            {isPro && (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-600 text-white rounded-full text-xs">
                <CheckCircle className="w-3 h-3" />
                All packs included with Pro
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        {packs.map((pack) => {
          const Icon = pack.icon;
          const isPurchased = purchasedPacks.includes(pack.id);
          const isActive = localStorage.getItem('tension_active_pack') === pack.id;

          return (
            <Card
              key={pack.id}
              className={`p-6 bg-gradient-to-br ${pack.bgColor} border-2 transition-all ${
                isActive ? 'border-purple-400 ring-2 ring-purple-200' : 'border-neutral-200'
              }`}
            >
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-3 bg-white rounded-lg ${pack.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-neutral-900">{pack.name}</h3>
                        {isActive && (
                          <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">{pack.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-3 h-3 text-neutral-500" />
                        <span className="text-xs text-neutral-500">{pack.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {isPro ? (
                      <div className="text-xs text-purple-600 font-medium">Included</div>
                    ) : isPurchased ? (
                      <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Purchased
                      </div>
                    ) : (
                      <div className="text-2xl font-light text-neutral-900">${pack.price}</div>
                    )}
                  </div>
                </div>

                {/* Expandable Details */}
                {selectedPack === pack.id && (
                  <div className="space-y-4 pt-4 border-t border-neutral-200/50">
                    {/* What's Included */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-light text-neutral-900">
                          {pack.includes.dailyActions}
                        </div>
                        <div className="text-xs text-neutral-600">Daily Actions</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-light text-neutral-900">
                          {pack.includes.exercises}
                        </div>
                        <div className="text-xs text-neutral-600">Exercises</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-light text-neutral-900">
                          {pack.includes.trackers}
                        </div>
                        <div className="text-xs text-neutral-600">Trackers</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-light text-neutral-900">
                          {pack.includes.resources}
                        </div>
                        <div className="text-xs text-neutral-600">Resources</div>
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <h4 className="text-sm font-medium text-neutral-900 mb-2">What You Get:</h4>
                      <ul className="space-y-2">
                        {pack.features.map((feature, idx) => (
                          <li key={idx} className="text-sm text-neutral-700 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {isPurchased || isPro ? (
                    <>
                      <Button
                        onClick={() => handleActivatePack(pack.id)}
                        className="flex-1"
                        variant={isActive ? 'outline' : 'default'}
                      >
                        {isActive ? 'Currently Active' : 'Activate Pack'}
                      </Button>
                      <Button
                        onClick={() => setSelectedPack(selectedPack === pack.id ? null : pack.id)}
                        variant="outline"
                      >
                        {selectedPack === pack.id ? 'Hide' : 'Details'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => handlePurchase(pack.id)}
                        className="flex-1 gap-2"
                        disabled={checkoutLoadingId === pack.id}
                      >
                        {checkoutLoadingId === pack.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          `Purchase $${typeof pack.price === 'number' ? pack.price.toFixed(2) : pack.price}`
                        )}
                      </Button>
                      <Button
                        onClick={() => setSelectedPack(selectedPack === pack.id ? null : pack.id)}
                        variant="outline"
                      >
                        {selectedPack === pack.id ? 'Hide' : 'Details'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Pro Upsell */}
      {!isPro && (
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Lock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-neutral-900 mb-1">
                Get All Packs with Pro
              </h3>
              <p className="text-sm text-neutral-600 mb-3">
                Upgrade to Pro and unlock all {packs.length} situational packs (${(packs.length * 2.99).toFixed(2)} value) 
                plus AI coaching, deep psychology, and unlimited features.
              </p>
              <Button
                size="sm"
                onClick={() => window.location.href = '/settings'}
                className="gap-2"
              >
                <ChevronRight className="w-4 h-4" />
                Upgrade to Pro $19.99/mo
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
